import React from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Grid,
  Divider,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import FacebookIcon from "@mui/icons-material/Facebook";
import { dash } from "./dashboardTokens";

function openFacebookConnect() {
  window.dispatchEvent(new Event("open-connect-facebook"));
}

/** Compact header action — avoids a full-width footer button. */
function FacebookHeaderAction({ embedded, disconnected }) {
  if (disconnected) {
    return (
      <Button
        size="small"
        variant="contained"
        disableElevation
        startIcon={<FacebookIcon sx={{ fontSize: 18 }} />}
        onClick={openFacebookConnect}
        sx={{
          flexShrink: 0,
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          px: 1.5,
          py: 0.5,
          borderRadius: 1.25,
          alignSelf: { xs: "flex-start", sm: "center" },
        }}
      >
        Connect Facebook
      </Button>
    );
  }

  return (
    <Button
      size="small"
      variant="text"
      color="primary"
      startIcon={<FacebookIcon sx={{ fontSize: 18 }} />}
      onClick={openFacebookConnect}
      sx={{
        flexShrink: 0,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        minWidth: 0,
        px: 1,
        py: 0.25,
        alignSelf: { xs: "flex-start", sm: "center" },
      }}
    >
      {embedded ? "Facebook Connected" : "Click to Connect Facebook"}
    </Button>
  );
}

export default function IntegrationStatusCard({ whatsapp }) {
  const embedded = whatsapp?.is_embedded_signup === 1;

  if (!whatsapp?.connected) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          border: `1px solid ${dash.border}`,
          boxShadow: dash.shadowSm,
          bgcolor: dash.surface,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: alpha("#64748b", 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CloudOffIcon sx={{ color: "text.disabled", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>WhatsApp not connected</Typography>
              <Typography variant="body2" sx={{ color: dash.textMuted, mt: 0.5, maxWidth: 520, lineHeight: 1.55, fontSize: 13 }}>
                Connect your Facebook account to link WhatsApp Business and enable messaging.
              </Typography>
            </Box>
          </Stack>
          <FacebookHeaderAction disconnected />
        </Stack>
      </Paper>
    );
  }

  const progress = whatsapp.sync_progress_percent;
  const hist = whatsapp.history_sync;
  const contact = whatsapp.contact_sync;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: `1px solid ${dash.border}`,
        boxShadow: dash.shadowSm,
        bgcolor: dash.surface,
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: embedded ? 2 : 0 }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: alpha("#2563eb", 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PhoneAndroidIcon sx={{ color: "primary.main", fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>WhatsApp Business</Typography>
              <Chip
                size="small"
                icon={<CloudDoneIcon sx={{ fontSize: "16px !important" }} />}
                label="Connected"
                sx={{
                  height: 26,
                  fontWeight: 600,
                  fontSize: 12,
                  bgcolor: alpha("#059669", 0.12),
                  color: "#047857",
                  border: "none",
                  "& .MuiChip-icon": { color: "#059669" },
                }}
              />
              {embedded && (
                <Chip
                  size="small"
                  label="Embedded signup"
                  sx={{
                    height: 26,
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: alpha("#2563eb", 0.1),
                    color: "primary.dark",
                    border: "none",
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1, color: dash.textMuted, fontSize: 13 }}>
              {whatsapp.phone_number || "Phone not on file"}
              {whatsapp.waba_id ? ` · WABA ${String(whatsapp.waba_id)}` : ""}
            </Typography>
          </Box>
        </Stack>
        <FacebookHeaderAction embedded={embedded} />
      </Stack>

      {embedded && (
        <>
          <Divider sx={{ borderColor: dash.border }} />
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: dash.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>
                History sync
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500, textTransform: "capitalize" }}>
                {hist != null ? String(hist).replace(/-/g, " ") : "—"}
              </Typography>
              {hist === "in-progress" && progress != null && (
                <Box sx={{ mt: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, progress))}
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      bgcolor: alpha("#2563eb", 0.12),
                      "& .MuiLinearProgress-bar": { borderRadius: 999 },
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: "block", color: dash.textMuted, fontFeatureSettings: '"tnum"' }}>
                    {Math.round(progress)}% complete
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: dash.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 10 }}>
                Contact sync
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500, textTransform: "capitalize" }}>
                {contact != null ? String(contact).replace(/-/g, " ") : "—"}
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
}
